/**
 * PR-LT3 variant A (A1) — encode a {@link DashPattern} into a 1-D
 * `THREE.DataTexture` the multi-element line fragment shader walks per element.
 *
 * # Why a texture (and why this encoding)
 *
 * Three.js `LineMaterial` exposes only a single `(dashSize, gapSize)` pair, so
 * CENTER / DASHDOT / DOT / PHANTOM collapsed to one dash/gap on-screen (the
 * LT3-B summary). To render the FULL signed pattern the fragment shader needs
 * the per-element cumulative offsets + kind flags; a small `DataTexture`
 * (width = element count, height = 1) carries them, sampled with
 * `texture2D` + a COMPUTED UV — NOT `texelFetch` — so the path stays
 * WebGL1-safe, mirroring `selectionStateTexture.ts` (Q18.B). `RGBAFormat` +
 * `UnsignedByteType` is the portable choice (float textures are not guaranteed
 * in WebGL1).
 *
 * # Texel layout (one texel per pattern element, left→right)
 *
 * For element `i` (signed length `e_i`, cumulative end offset
 * `c_i = Σ_{k≤i} |e_k|`):
 *
 * | channel | meaning                                                        |
 * |---------|----------------------------------------------------------------|
 * | R       | high byte of `cumEndNorm_i = c_i / totalLength` (16-bit, ÷65535)|
 * | G       | low  byte of `cumEndNorm_i`                                     |
 * | B       | kind flag: 0 = gap, 127 = dot, 254 = dash (shader maps to −1/0/+1)|
 * | A       | element's own normalized length `|e_i| / totalLength` × 255     |
 *
 * The 16-bit R+G packing of the cumulative offset keeps dash boundaries aligned
 * (8-bit alone is ~0.4% of the period — visible drift on a long CENTER dash).
 * `cumEndNorm` for the LAST element is exactly 1.0 by construction.
 *
 * The shader recovers, for normalized period position `p ∈ [0,1)`:
 *   - the first element `i` with `p < cumEndNorm_i`,
 *   - its kind (gap → discard, dash → keep, dot → keep only a tiny round mark).
 *
 * # Lifetime / cache
 *
 * One texture per DISTINCT pattern per bulk-load pass. `LinetypePatternTextureCache`
 * keys by linetype NAME so repeated buckets (many layers sharing CENTER) reuse
 * the same texture. The cache's textures are disposed together with the buckets
 * that reference them (mirrors the per-bucket selection-state texture lifecycle;
 * see `sceneTeardown.ts`).
 */

import * as THREE from "three";

import type { DashPattern } from "./linetypePatterns";

/**
 * Hard cap on element count — matches the shader's `MAX_DASH_ELEMENTS` constant
 * loop bound. Real AutoCAD linetypes top out well below this (PHANTOM = 6).
 * Patterns longer than the cap are truncated (documented; never hit in practice)
 * so the texture width always fits the bounded shader walk.
 */
export const MAX_DASH_ELEMENTS = 16;

/** Kind-flag byte values written into the B channel (shader maps to −1/0/+1). */
const KIND_GAP = 0;
const KIND_DOT = 127;
const KIND_DASH = 254;

/**
 * Result of encoding a pattern: the `DataTexture` plus the scalar uniforms the
 * material must set alongside it.
 */
export interface DashPatternTextureData {
  texture: THREE.DataTexture;
  /** Element count = texture width (≤ {@link MAX_DASH_ELEMENTS}). */
  elementCount: number;
  /** Repeat period in DRAWING UNITS (the shader divides `vLineDistance` by it). */
  patternLength: number;
}

/**
 * Encode a {@link DashPattern} into a width-N RGBA8 `DataTexture` (see the
 * module header for the texel layout). The texture uses NEAREST filtering so
 * each texel comes back un-interpolated.
 */
export function buildDashPatternTexture(
  pattern: DashPattern,
): DashPatternTextureData {
  const elements = pattern.elements.slice(0, MAX_DASH_ELEMENTS);
  const count = elements.length;

  // FIX-4 (Codex P2) — when a pattern has MORE than MAX_DASH_ELEMENTS elements
  // it is truncated to the first N. The cumulative ends + the returned period
  // MUST be normalized against the SUM of the KEPT elements' |length|, NOT the
  // original `pattern.totalLength`. Otherwise every cumulative end is scaled
  // down by (truncatedTotal / originalTotal), the last encoded texel's cumEnd
  // is < 1.0, and the shader (which loops only over the encoded texels)
  // discards the un-encoded tail of every repeat as a gap. Recomputing the
  // period from the kept elements makes the on-screen repeat match what is
  // actually encoded. Standard linetypes are ≤ ~12 elements so this only fires
  // for rare custom patterns; the >16-element tail is intentionally dropped
  // (documented on MAX_DASH_ELEMENTS).
  let keptTotal = 0;
  for (let i = 0; i < count; i++) keptTotal += Math.abs(elements[i] ?? 0);
  const truncated = count < pattern.elements.length;
  const total = truncated
    ? keptTotal > 0
      ? keptTotal
      : 1
    : pattern.totalLength > 0
      ? pattern.totalLength
      : 1;

  const data = new Uint8Array(count * 4);
  let cum = 0;
  for (let i = 0; i < count; i++) {
    const e = elements[i] ?? 0;
    cum += Math.abs(e);
    // Cumulative end offset, normalized to the period, packed 16-bit.
    let cumNorm = cum / total;
    if (cumNorm > 1) cumNorm = 1;
    if (cumNorm < 0) cumNorm = 0;
    const packed = Math.round(cumNorm * 65535);
    const hi = (packed >> 8) & 0xff;
    const lo = packed & 0xff;
    // Kind flag by SIGN: >0 dash, <0 gap, ==0 dot (DXF group-49 semantics).
    const kind = e > 0 ? KIND_DASH : e < 0 ? KIND_GAP : KIND_DOT;
    // Element's own normalized length (for the dot's round-mark width).
    let lenNorm = Math.abs(e) / total;
    if (lenNorm > 1) lenNorm = 1;
    const base = i * 4;
    data[base] = hi;
    data[base + 1] = lo;
    data[base + 2] = kind;
    data[base + 3] = Math.round(lenNorm * 255);
  }

  const texture = new THREE.DataTexture(
    data,
    count,
    1,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  );
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  // FIX-4 — return the TRUNCATED period when the pattern was clipped to N=16 so
  // the shader's `mod(vLineDistance, patternLength)` repeat matches the encoded
  // texels. Un-truncated patterns return the authored `totalLength` unchanged.
  return { texture, elementCount: count, patternLength: total };
}

/**
 * Stable cache key for a pattern — derived from its CONTENT (signed elements +
 * period), NOT a linetype name.
 *
 * # Why content, not name
 *
 * A `ByLayer` bucket keeps the sentinel name `"ByLayer"` in its MeshKey (the
 * layer is a separate key field — see `batchedLayer.extractLinetype`), so two
 * buckets on different layers (CENTER vs DASHDOT) share the name `"ByLayer"`
 * yet resolve to DIFFERENT patterns. Keying by name would collide and paint one
 * layer with the other's pattern. The content signature is collision-free and
 * still dedups identical patterns (many layers → CENTER reuse one texture).
 */
function patternCacheKey(pattern: DashPattern): string {
  return `${pattern.totalLength}|${pattern.elements.join(",")}`;
}

/**
 * Per-pass cache: pattern CONTENT signature → its encoded pattern texture.
 * Built once per bulk-load pass and shared across buckets that resolve to the
 * same pattern (many layers → CENTER). Buckets reference (do NOT own) the
 * texture; the cache owns disposal via {@link dispose}.
 */
export class LinetypePatternTextureCache {
  private readonly byKey = new Map<string, DashPatternTextureData>();

  /**
   * Return the cached texture for `pattern`, building it on first request. The
   * key is the pattern's CONTENT signature (see {@link patternCacheKey}), so
   * identical patterns reuse one `THREE.DataTexture` regardless of the linetype
   * name (incl. distinct `ByLayer` buckets that resolve to the same pattern).
   */
  acquire(pattern: DashPattern): DashPatternTextureData {
    const key = patternCacheKey(pattern);
    let entry = this.byKey.get(key);
    if (!entry) {
      entry = buildDashPatternTexture(pattern);
      this.byKey.set(key, entry);
    }
    return entry;
  }

  /** Number of distinct textures currently cached (for tests / diagnostics). */
  get size(): number {
    return this.byKey.size;
  }

  /** Dispose every cached texture and clear the cache. */
  dispose(): void {
    for (const entry of this.byKey.values()) entry.texture.dispose();
    this.byKey.clear();
  }
}

/**
 * Module-scope shared cache.
 *
 * # Why module-scope (vs per-pass instance)
 *
 * Mirrors the hatch pattern-texture cache (`hatch/hatchPatterns.ts`
 * `clearPatternTextureCache`): a small set of distinct linetype patterns
 * (CENTER / DASHDOT / DOT / PHANTOM + a handful of customs) is reused across
 * EVERY bucket and across bulk-loads. A module-scope cache dedups them all
 * (one `DataTexture` per distinct pattern, regardless of how many buckets or
 * relift passes reference it) without threading a per-pass instance through the
 * bulk-load loop AND the per-entity relift path (which materialises buckets
 * one at a time, outside any pass scope). Textures are tiny (≤16×1 RGBA8 = 64
 * bytes) so retaining the working set across bulk-loads is negligible; the
 * cache is cleared explicitly on hot reload via
 * {@link clearLinetypePatternTextureCache} (same lifecycle as the hatch cache).
 */
const SHARED_PATTERN_TEXTURE_CACHE = new LinetypePatternTextureCache();

/** The shared module-scope linetype pattern-texture cache. */
export function getLinetypePatternTextureCache(): LinetypePatternTextureCache {
  return SHARED_PATTERN_TEXTURE_CACHE;
}

/**
 * Dispose + clear the shared cache. Called on hot reload only (same as the
 * hatch `clearPatternTextureCache`) — bucket meshes that still reference a
 * disposed texture are themselves torn down in the same hot-reload pass.
 */
export function clearLinetypePatternTextureCache(): void {
  SHARED_PATTERN_TEXTURE_CACHE.dispose();
}
